<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<app-tab-item :tab="tab" value="variances">
					<app-heading :title="`${market.name} Variances Report`" />
					<app-filter :value="hasFilters" @click="applyFilters()">
						<app-row no-padding>
							<app-cell cols="6" sm="3">
								<input-select
									v-model="filters.city"
									label="City"
									:items="datasources.cities"
									clearable
									dense
								></input-select>
							</app-cell>
							<app-cell cols="6" sm="3">
								<input-select
									v-model="filters.area"
									label="Area"
									:items="datasources.areas"
									clearable
									dense
								></input-select>
							</app-cell>
							<app-cell cols="6" sm="3">
								<input-select
									v-model="filters.complex"
									label="Complex"
									:items="datasources.complexes"
									clearable
									dense
								></input-select>
							</app-cell>
							<app-cell cols="6" sm="3">
								<input-select
									v-model="filters.bedrooms"
									label="Bedrooms"
									:items="datasources.bedrooms"
									clearable
									dense
								></input-select>
							</app-cell>
						</app-row>
					</app-filter>
					<app-row>
						<app-cell>
							<app-data-table
								:items="participants"
								:headers="headers"
								:total="count"
								:refresh.sync="refresh"
								:initial-state="tableState"
								sort-by="last_name"
								@update="fetchParticipants"
							/>
						</app-cell>
					</app-row>
					<app-floating-button
						class="d-inline-flex d-sm-none"
						:to="{ name: 'market-city', params: { city: 'new' } }"
						color="success"
						icon="fa-plus"
					/>
				</app-tab-item>
			</template>
		</app-tabs>
	</app-page>
</template>

<script>
import { filters, search, tableState } from '@/mixins'
import { marketMathTabs } from '@/datasources/tabs'

const LISTINGS_BATCH_LIMIT = 100
const SURVEYS_BATCH_LIMIT = 100
const BEDROOMS_TRESHOLD = 7

export default {
	mixins: [filters, search, tableState],
	data() {
		return {
			market: {
				name: '',
			},
			participants: [],
			count: -1,
			tabs: [],
			datasources: {
				cities: [],
				areas: [],
				complexes: [],
				bedrooms: [{
					text: 'Studio',
					value: 0,
				}, 1, 2, 3, 4, 5, 6, {
					text: '7+',
					value: 7,
				}],
			},
			refresh: false,
			headers: [{
				text: 'First Name',
				value: 'first_name',
				sortable: true,
			}, {
				text: 'Last Name',
				value: 'last_name',
				sortable: true,
			}, {
				text: 'Revenue',
				value: 'revenue',
				sortable: false,
				cb: item => (item.revenue ? `${(item.revenue * 100).toFixed(2)}%` : 'N/A'),
			}, {
				text: 'ADR',
				value: 'adr',
				sortable: false,
				cb: item => (item.adr ? `${(item.adr * 100).toFixed(2)}%` : 'N/A'),
			}, {
				text: 'Occupancy Rate',
				value: 'occupancy_rate',
				cb: item => (item.occupancy_rate ? `${(item.occupancy_rate * 100).toFixed(2)}%` : 'N/A'),
				sortable: false,
			}],
			defaultTableSort: 'last_name',
			filters: {
				city: '',
				area: '',
				complex: '',
				bedrooms: '',
			},
		}
	},
	computed: {
		marketId() {
			return this.$route.params.market
		},
	},
	async mounted() {
		this.market = await this.$ravello.graphql('market', this.marketId)
			.fields(['name'])
			.query()

		const datasources = await this.$ravello.graphql('cities')
			.parent(this.$ravello.graphql('market', this.marketId))
			.datasource()
			.sort('name')
			.union(this.$ravello.graphql('areas')
				.parent(this.$ravello.graphql('market', this.marketId))
				.sort('name')
				.datasource())
			.union(this.$ravello.graphql('complexes')
				.parent(this.$ravello.graphql('market', this.marketId))
				.sort('name')
				.datasource())
			.query(true)

		this.datasources.cities = datasources.cities
		this.datasources.areas = datasources.areas
		this.datasources.complexes = datasources.complexes
		this.tabs = marketMathTabs(this.marketId)
	},
	methods: {
		async getListings(offset) {
			const request = this.$ravello.graphql('listings')
				.parent(this.$ravello.graphql('market', this.marketId))
				.fields(['_id', 'revenue', 'occupancy_rate'])
				.include(this.$ravello.graphql('survey')
					.fields(['_id']))
				.filter('validated', true)
				.offset(offset)
				.limit(LISTINGS_BATCH_LIMIT)

			// Applied filters
			if (this.appliedFilters.city) {
				request.filter('market_city', this.appliedFilters.city)
			}
			if (this.appliedFilters.area) {
				const areaComplexes = await this.$ravello.graphql('complexes')
					.parent(this.$ravello.graphql('market', this.marketId))
					.datasource()
					.filter('area', this.appliedFilters.area)
					.query()
				request.filter('complex', areaComplexes.map(areaComplex => areaComplex.value))
			}
			if (this.appliedFilters.complex) {
				request.filter('complex', this.appliedFilters.complex)
			}
			if (this.appliedFilters.bedrooms !== undefined) {
				request.filter('bedrooms', this.appliedFilters.bedrooms >= BEDROOMS_TRESHOLD ? {
					gte: BEDROOMS_TRESHOLD,
				} : this.appliedFilters.bedrooms)
			}

			const listings = await request.query()
			return listings
		},

		async getSurveys(ids, offset) {
			const request = this.$ravello.graphql('surveys')
				.parent(this.$ravello.graphql('market', this.marketId))
				.include(this.$ravello.graphql('results')
					.fields(['score', 'revenue', 'occupancy_rate'])
					.include(this.$ravello.graphql('participant')
						.fields(['_id']))
					.include(this.$ravello.graphql('listing')
						.fields(['_id']))
					.limit(this.participants.length * 25))
				.offset(offset)
				.filter('_id', ids)
				.sort('_id')
				.limit(LISTINGS_BATCH_LIMIT)

			const listings = await request.query()
			return listings
		},

		getVariance(src, adjusted) {
			return adjusted ? (src - adjusted) / adjusted : 0
		},

		injectVariancesToParticipants(participants, survey, listingsMap) {
			const { results } = survey
			results.forEach((result) => {
				if (!result.participant || !result.listing) { return }
				const participantRef = participants.find(participant => participant._id === result.participant._id)
				if (!participantRef) { return }
				if (!participantRef.variances) {
					participantRef.variances = []
				}

				const listingAdjustedData = listingsMap[result.listing._id]
				if (listingAdjustedData) {
					const participantAdr = result.occupancy_rate ? (result.revenue / (365 * (result.occupancy_rate / 100))) : 0
					participantRef.variances.push({
						listing: result.listing._id,
						revenue: this.getVariance(result.revenue, listingAdjustedData.revenue),
						occupancyRate: this.getVariance(result.occupancy_rate, listingAdjustedData.occupancyRate),
						adr: this.getVariance(participantAdr, listingAdjustedData.adr),
					})
				}
			})
		},

		injectVarianceAveragesToParticipantes(participants) {
			participants.forEach((participant) => {
				if (!participant.variances) { return }
				const total = participant.variances.length
				participant.revenue = participant.variances.reduce((acc, variance) => acc + (variance.revenue || 0), 0) / total
				participant.occupancy_rate = participant.variances.reduce((acc, variance) => acc + (variance.occupancyRate || 0), 0) / total
				participant.adr = participant.variances.reduce((acc, variance) => acc + (variance.adr || 0), 0) / total
			})
		},

		async computeVariances(participants) {
			const surveyIdsMap = {}
			const listingsMap = {}

			let listings = []
			let offset = 0
			do {
				// eslint-disable-next-line no-await-in-loop
				listings = await this.getListings(offset)
				listings.forEach((listing) => {
					if (!listing.survey) { return }
					surveyIdsMap[listing.survey._id] = true
					listingsMap[listing._id] = {
						revenue: listing.revenue,
						occupancyRate: listing.occupancy_rate,
						adr: listing.occupancy_rate ? (listing.revenue / (365 * (listing.occupancy_rate / 100))) : null,
					}
				})
				offset += LISTINGS_BATCH_LIMIT
			} while (listings.length === LISTINGS_BATCH_LIMIT)

			let surveys = []
			const surveyIds = Object.keys(surveyIdsMap)
			offset = 0
			do {
				// eslint-disable-next-line no-await-in-loop
				surveys = await this.getSurveys(surveyIds, offset)
				surveys.forEach((survey) => {
					this.injectVariancesToParticipants(participants, survey, listingsMap)
				})
				offset += SURVEYS_BATCH_LIMIT
			} while (surveys.length === SURVEYS_BATCH_LIMIT)

			this.injectVarianceAveragesToParticipantes(participants)
		},

		async fetchParticipants(pagination) {
			this.updateTableState(pagination)

			const request = this.$ravello.graphql('participants')
				.parent(this.$ravello.graphql('market', this.marketId))
				.fields(['_id', 'first_name', 'last_name'])
				.fromPagination(pagination)

			if (this.count === -1) {
				request.count('count')
			}
			try {
				const participants = await request.query()
				if (this.count === -1) {
					this.count = request.results.count
					await this.computeVariances(participants)
				}
				this.participants = participants
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},
	},
}
</script>
